import React, { useState, useEffect } from "react";
import "./index.scss";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import { Pagination, Stack } from "@mui/material";
import { getNestedPathValue } from "../../utils/misc2";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

const MuiDenseTable = ({
  dataList = [],
  columnsList = [],
  pageCount = 1,
  onPageChange,
  filters = {},
  onRowClick,
  maxWords = 10,
  tableClass = "",
  tableContainerClass = "",
  onExpandRow,
  firstColumn = "particular",
  isSmallTable = false,
}) => {
  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (index, firstColumnValue) => {
    setExpandedRows((prev) => ({
      ...prev,
      [firstColumnValue]: !prev[firstColumnValue],
    }));
    onExpandRow(firstColumnValue);
  };

  useEffect(() => {
    if (Number.isInteger(parseInt(filters?.page))) {
      setPage(filters.page);
    }
  }, [filters]);

  const truncateText = (text, maxWords) => {
    const words = text?.split(" ");
    return words.length <= maxWords
      ? text
      : words.slice(0, maxWords).join(" ") + "...";
  };

  // Compute cumulative left offsets for frozen columns
  let cumulativeWidth = 0;
  const columnWidths = columnsList.map((col) => {
    const width = col.width || 100;
    const result = { id: col.id, width };
    if (col.freeze) {
      result.left = cumulativeWidth;
      cumulativeWidth += width;
    }

    return result;
  });

  // Helper for sticky/freeze styles
  const getStickyStyle = (colId, isHeader = false) => {
    const col = columnWidths.find((c) => c.id === colId);
    if (!col || col.left === undefined) return {};
    return {
      position: "sticky",
      left: `${col.left}px`,
      background: "white",
      zIndex: isHeader ? 20 : 5, // higher zIndex for headers
    };
  };

  const hasGroupedHeader = columnsList.some((col) => col.columns);

  return (
    <div className={`custom-dense-mui-table ${tableClass}`}>
      <div className="table-scroll-wrapper">
        <TableContainer
          component={Paper}
          className={`mui-table-container ${tableContainerClass}`}
          sx={{ maxHeight: 600 }}
        >
          <Table size="small" className="dense-table" stickyHeader>
            <TableHead>
              <>
                <TableRow
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 30,
                    background: "white",
                  }}
                >
                  {columnsList.map((col) => (
                    <TableCell
                      key={col.id}
                      align="center"
                      padding={col?.headPadding ? col.headPadding : "none"}
                      colSpan={col.columns ? col.columns.length : 1}
                      rowSpan={col.columns ? 1 : 2}
                      style={{
                        background: col.background || "white",
                        width: col.width || 100,
                      }}
                      sx={col.freeze ? getStickyStyle(col.id, true) : {}}
                    >
                      {col.headerRender ? col.headerRender() : col.label}
                    </TableCell>
                  ))}
                </TableRow>
                {hasGroupedHeader && (
                  <TableRow
                    sx={{
                      position: "sticky",
                      top: 23, // height of first header row (adjust if your header row is taller)
                      zIndex: 25,
                      background: "white",
                    }}
                  >
                    {columnsList.map((col) =>
                      col.columns
                        ? col.columns.map((subCol) => (
                            <TableCell
                              key={subCol.id}
                              align="center"
                              style={{
                                background:
                                  subCol.headerBackground ||
                                  col.background ||
                                  "white",
                                width: subCol.width || 100,
                              }}
                              sx={
                                subCol.freeze
                                  ? getStickyStyle(subCol.id, true)
                                  : {}
                              }
                            >
                              {subCol.label}
                            </TableCell>
                          ))
                        : null
                    )}
                  </TableRow>
                )}
              </>
            </TableHead>
            <TableBody>
              {dataList.map((row, rowIndex) => {
                if (!row.showNow) {
                  return;
                }

                return (
                  <React.Fragment key={rowIndex}>
                    <TableRow onClick={() => onRowClick?.(row)}>
                      {/* First column (particular) */}
                      {columnsList?.find((c) => c.id === firstColumn) && (
                        <TableCell
                          sx={{
                            ...(columnsList.find((c) => c.id === firstColumn)
                              ?.freeze
                              ? getStickyStyle(firstColumn)
                              : {}),
                            ...(isSmallTable ? { padding: "2px 4px" } : {}),
                          }}
                          style={{
                            fontWeight: row?.bold ? "bold" : "normal",
                            paddingLeft: row.parent
                              ? isSmallTable
                                ? "12px"
                                : "20px"
                              : isSmallTable
                              ? "4px"
                              : "8px",
                            paddingTop: isSmallTable ? undefined : "0px",
                            paddingBottom: isSmallTable ? undefined : "0px",
                            fontSize: isSmallTable ? "12px" : "14px",
                            minWidth:
                              columnsList.find((c) => c.id === firstColumn)
                                ?.width || 200,
                          }}
                        >
                          <Stack flexDirection="row" alignItems="center">
                            {row.child ? (
                              <Stack
                                flexDirection={"row"}
                                alignItems="center"
                                style={
                                  row.parent
                                    ? { paddingLeft: "14px", cursor: "pointer" }
                                    : {}
                                }
                                onClick={() =>
                                  toggleRow(rowIndex, row[firstColumn])
                                }
                              >
                                {expandedRows[row[firstColumn]] ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )}
                                <span
                                  style={
                                    row.parent
                                      ? {
                                          color: row?.[firstColumn]?.includes(
                                            "%"
                                          )
                                            ? "blue"
                                            : "green",
                                          fontStyle: "italic",
                                        }
                                      : {}
                                  }
                                >
                                  {row[firstColumn]}
                                </span>
                              </Stack>
                            ) : (
                              <span
                                style={
                                  row.parent && !row.child
                                    ? {
                                        paddingLeft:
                                          row?.level === 2 ? "50px" : "38px",
                                        color: "green",
                                        fontWeight: row?.bold
                                          ? "bold"
                                          : "normal",
                                      }
                                    : {
                                        paddingLeft: "24px",
                                        fontWeight: row?.bold
                                          ? "bold"
                                          : "normal",
                                      }
                                }
                              >
                                {row[firstColumn]}
                              </span>
                            )}
                          </Stack>
                        </TableCell>
                      )}

                      {/* Other columns */}
                      {columnsList.map((col, colIndex) => {
                        if (col.id !== firstColumn) {
                          let value =
                            getNestedPathValue(col.id, row) ||
                            row[col.id] ||
                            "-";

                          // Apply conversion logic if available
                          value =
                            typeof col.valueConversionLogic === "function"
                              ? col.valueConversionLogic(value)
                              : value;

                          // Handle custom rendering
                          if (col.render) {
                            return (
                              <TableCell
                                key={col.id || colIndex}
                                className="MuiTableCell"
                                sx={{
                                  ...(col.freeze ? getStickyStyle(col.id) : {}),
                                  ...(isSmallTable
                                    ? { padding: "2px 4px", fontSize: "12px" }
                                    : {}),
                                }}
                                style={{
                                  width: col.width || 120,
                                }}
                              >
                                {col.render(row)}
                              </TableCell>
                            );
                          }

                          // Handle nested columns
                          if (col.columns) {
                            return col.columns.map((subCol) => (
                              <TableCell
                                key={subCol.id}
                                align="center"
                                className="MuiTableCell"
                                sx={{
                                  ...(subCol.freeze
                                    ? getStickyStyle(subCol.id)
                                    : {}),
                                  ...(isSmallTable
                                    ? { padding: "2px 4px", fontSize: "12px" }
                                    : {}),
                                }}
                                style={{ width: subCol.width || 120 }}
                              >
                                {subCol.render
                                  ? subCol.render(row)
                                  : row[col.id]?.map((item) => (
                                      <div key={item._id}>
                                        {item[subCol.id]}
                                      </div>
                                    ))}
                              </TableCell>
                            ));
                          }

                          return (
                            <TableCell
                              key={col.id || colIndex}
                              className="MuiTableCell"
                              sx={{
                                ...(col.freeze ? getStickyStyle(col.id) : {}),
                                ...(isSmallTable
                                  ? { padding: "2px 4px", fontSize: "12px" }
                                  : {}),
                              }}
                              style={{ width: col.width || 120 }}
                            >
                              {maxWords && value?.split(" ").length > maxWords
                                ? truncateText(value, maxWords)
                                : value}
                            </TableCell>
                          );
                        }
                        return null;
                      })}
                    </TableRow>

                    {/* Expanded rows */}
                    {expandedRows[rowIndex] &&
                      row.nestedColumns?.map((nested, nestedIndex) => (
                        <TableRow key={`${rowIndex}-${nestedIndex}`}>
                          <TableCell
                            sx={{
                              ...(columnsList.find((c) => c.id === firstColumn)
                                ?.freeze
                                ? getStickyStyle(firstColumn)
                                : {}),
                              ...(isSmallTable
                                ? { padding: "2px 4px", fontSize: "12px" }
                                : {}),
                            }}
                            style={{ paddingLeft: isSmallTable ? 20 : 30 }}
                          >
                            {nested.item}
                          </TableCell>
                          {columnsList.map((col) => {
                            if (col.id !== firstColumn) {
                              const nestedValue = nested[col.id] || "-";
                              return (
                                <TableCell
                                  key={col.id}
                                  sx={{
                                    ...(col.freeze
                                      ? getStickyStyle(col.id)
                                      : {}),
                                    ...(isSmallTable
                                      ? { padding: "2px 4px", fontSize: "12px" }
                                      : {}),
                                  }}
                                >
                                  {nestedValue}
                                </TableCell>
                              );
                            }
                            return null;
                          })}
                        </TableRow>
                      ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {pageCount > 1 && (
        <Pagination
          page={page}
          count={pageCount}
          color="primary"
          onChange={(e, newPage) => {
            setPage(newPage);
            onPageChange?.(newPage);
          }}
        />
      )}
    </div>
  );
};

export default MuiDenseTable;
